import React from 'react';
import { CheckTreePicker } from 'rsuite';
import { ItemDataType } from 'rsuite/CascadeTree';
import { ProductListFilter } from '../../api/apiTypes';
import { ApiGroup } from '../../api/model/ApiGroup';
import { Eshop } from '../../store/catalogs/types';

const BASE_MARKETPLACE_SUFFIX = ' (base)';

interface Props {
  eshops: Eshop[];
  value: number[];
  onChange: (selectedEshopIds: number[]) => void;
}

function isAdditionalMarketplace(eshop: Eshop): boolean {
  return !!eshop.baseMarketplaceCode;
}

function shouldShowBaseSuffix(eshop: Eshop): boolean {
  return eshop.apiGroup === ApiGroup.ALLEGRO;
}

function mapEshopsToTreeData(eshops: Eshop[]): ItemDataType<number>[] {
  return eshops
    .filter(eshop => !isAdditionalMarketplace(eshop))
    .map(base => {
      const additionals = eshops.filter(eshop => eshop.baseMarketplaceCode === base.code);
      const label = shouldShowBaseSuffix(base)
        ? `${base.name}${BASE_MARKETPLACE_SUFFIX}`
        : base.name;

      if (additionals.length === 0) {
        return { value: base.id, label };
      }

      return {
        value: base.id,
        label,
        children: additionals.map(additional => ({
          value: additional.id,
          label: additional.name,
        })),
      };
    });
}

function getDisabledEshopIds(eshops: Eshop[], selectedEshopIds: number[]): number[] {
  const selectedCodes = eshops
    .filter(eshop => selectedEshopIds.includes(eshop.id))
    .map(eshop => eshop.code);

  return eshops
    .filter(eshop => {
      const { baseMarketplaceCode } = eshop;

      return !!baseMarketplaceCode && !selectedCodes.includes(baseMarketplaceCode);
    })
    .map(eshop => eshop.id);
}

function removeUnavailableAdditionals(eshops: Eshop[], selectedEshopIds: number[]): number[] {
  const disabledEshopIds = getDisabledEshopIds(eshops, selectedEshopIds);

  return selectedEshopIds.filter(eshopId => !disabledEshopIds.includes(eshopId));
}

export function mapSelectedMarketplaces(
  eshops: Eshop[],
  selectedEshopIds: number[],
): Pick<ProductListFilter, 'eshopIds' | 'additionalMarketplaceCodes'> {
  const selectedEshops = eshops.filter(eshop => selectedEshopIds.includes(eshop.id));

  return {
    eshopIds: selectedEshops
      .filter(eshop => !isAdditionalMarketplace(eshop))
      .map(eshop => eshop.id),
    additionalMarketplaceCodes: selectedEshops
      .filter(isAdditionalMarketplace)
      .map(eshop => eshop.code),
  };
}

export default function MarketplaceTreePicker({ eshops, value, onChange }: Props) {
  function handleChange(selectedEshopIds: number[]) {
    onChange(removeUnavailableAdditionals(eshops, selectedEshopIds));
  }

  return (
    <CheckTreePicker
      data={mapEshopsToTreeData(eshops)}
      value={value}
      disabledItemValues={getDisabledEshopIds(eshops, value)}
      onChange={values => handleChange(values as number[])}
      cascade={false}
      searchable={false}
      block
    />
  );
}
